import { Arr } from '@deqo/foundation';
import { Button, LoadingOverlay, Pagination } from '@mantine/core';
import React, { useCallback, useMemo, useState } from 'react';
import { JiraIssueCreateResult, JiraProjectData } from '../../../api/endpoints/jira';
import { Icon } from '../../../concepts/resources/Icon';
import { SavingIndicator } from '../../../ui/SavingIndicator';
import { FlexScroller } from '../../../ui/Scrollers';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { CreateJiraIssueForm } from './CreateJiraIssueForm';
import { useCreateJiraIssueMutation, useIssueTypeState, useJiraIssueCreateSchemaQuery } from './CreateJiraIssueHook';
import { JiraIssueTypeInput } from './inputs/JiraIssueTypeInput';

export interface CreateJiraIssueSource {
    localId?: string;
    summary?: string;
    description?: string;
}

export type CreateSingleJiraIssueView = {
    jiraProject: JiraProjectData,
    sources?: CreateJiraIssueSource[],
    onComplete: (info: JiraIssueCreateResult[]) => void,
};

const defaultItem = () => ({
    summary: '{summary}',
    description: '{description}',
});

export const CreateMultiSingleJiraIssueView = ({ jiraProject, sources, onComplete }: CreateSingleJiraIssueView) => {

    const [issueType, setIssueType] = useIssueTypeState(jiraProject);
    const schema = useJiraIssueCreateSchemaQuery(jiraProject.id, issueType.id);
    const create = useCreateJiraIssueMutation(jiraProject.id);

    const [items, setItems] = useState([defaultItem(), ...sources]);
    const [activeIndex, setActiveIndex] = useState(0);

    const activeItem = useMemo(() => {
        // Merge actual item with defaults
        const base = items[0];
        if (activeIndex === 0) return base;
        const item = items[activeIndex];
        return {
            ...base,
            ...item,
        };
    }, [items, activeIndex]);

    const [error, setError] = useState<string | null>(null);
    const [errorFields, setErrorFields] = useState([] as string[]);

    const onChangeActiveItem = useCallback((fieldId: string, val: any) => {
        if (activeIndex < 0 || activeIndex >= items.length) return;
        setItems(
            items.map((item, i) =>
                i === activeIndex ? ({ ...item, [fieldId]: val }) : item
            )
        );

    }, [activeItem]);

    const onCreate = () => {
        const base = items[0];
        const finalItems = items.slice(1).map((item) => ({
            ...base,
            ...item,
            issueType,
        }));

        create.mutateAsync(finalItems).then((reply) => {
            const anyOk = reply.some(x => x.ok);
            if (anyOk) {
                onComplete(reply);
            }
            else {
                setError(reply?.[0]?.errors?.[0] ?? 'Unknown error');
                setErrorFields(
                    Arr.unique(reply.flatMap(x => x.errors).map(x => x.split(':')[0]))
                );
            }
        });
    };

    const onPrev = () => {
        setActiveIndex(Math.max(0, activeIndex - 1));
    };
    const onNext = () => {
        setActiveIndex(Math.min(items.length, activeIndex + 1));
    };

    return <Flexer relative grow flex="column" gap="1" width="54">
        <LoadingOverlay visible={schema.isFetching} zIndex={1000} overlayProps={{ radius: 'sm', blur: 2 }} />
        <JiraIssueTypeInput
            label="Create Issues As"
            allowedValues={jiraProject.issueTypes}
            value={issueType}
            onChange={setIssueType}
        />
        <Pagination.Root total={items.length - 1} value={activeIndex} onChange={setActiveIndex}>
            <Flexer flex="row" align="center" gap=".375">
                <Pagination.Control onClick={onPrev} disabled={activeIndex === 0}>
                    <Icon size="1" src="deqo://icons/hero/arrow-left" />
                </Pagination.Control>
                <Pagination.Control active={activeIndex === 0} onClick={() => setActiveIndex(0)}>
                    Defaults
                </Pagination.Control>
                <Pagination.Items />
                <Pagination.Control onClick={onNext} disabled={activeIndex >= items.length - 1}>
                    <Icon size="1" src="deqo://icons/hero/arrow-right" />
                </Pagination.Control>
            </Flexer>
        </Pagination.Root>
        <Flexer grow flex="column" gap="1" margin="0 -.75">
            <FlexScroller shadow>
                <Flexer grow flex="column" gap="1" padding="0 .75">
                    {activeItem && <CreateJiraIssueForm
                        key={activeIndex}
                        fields={schema.data}
                        values={activeItem}
                        errors={errorFields}
                        onChange={onChangeActiveItem}
                    />}
                </Flexer>
            </FlexScroller>
        </Flexer>
        <Flexer flex="row" align="center" justify="space-between">
            <Flexer>
                {error && <SavingIndicator state="error" errorText={error} />}
            </Flexer>
            <Button variant="primary" loading={create.isLoading} onClick={onCreate}>
                Create Issues ({items.length - 1})
            </Button>
        </Flexer>
    </Flexer>;
};
